import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { X, Building2, User, MapPin, Clock, CheckCircle, ExternalLink } from "lucide-react"
import { toast } from "./Toast"

type FortyEvent = {
  start?: string
  end?: string
  monteur_name?: string | null
  location?: string | null
  project_id?: number | null
  in_ours?: boolean
}

type FortyDetail = {
  customer_id?: number | null
  facility_id?: number | null
  kunde_name?: string | null
  kunde_number?: number | null
  objekt_name?: string | null
  street?: string | null
  zip?: string | null
  city?: string | null
  web_link?: string | null
}

function fmt(iso?: string): string {
  if (!iso) return ""
  try {
    const d = new Date(iso)
    return d.toLocaleString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
  } catch { return iso }
}

/**
 * Detailansicht eines fortytools-Einsatzes im Kalender. Zeigt Monteur/Zeit/Ort
 * + Kunde/Objekt (aufgelöst über project_id). Die Adresse (Objekt bzw. Kunde)
 * ist editierbar (PATCH /api/v1/calendar/forty-adresse), plus Deep-Link nach
 * fortytools. Der Einsatz selbst wird in fortytools gepflegt.
 */
export function FortyEventModal({
  open, onClose, tenant, event,
}: {
  open: boolean
  onClose: () => void
  tenant: string
  event: FortyEvent | null
}) {
  const qc = useQueryClient()
  const [edit, setEdit] = useState(false)
  const [street, setStreet] = useState("")
  const [zip, setZip] = useState("")
  const [city, setCity] = useState("")
  const [err, setErr] = useState<string | null>(null)

  const detail = useQuery<FortyDetail>({
    queryKey: ["forty-detail", tenant, event?.project_id],
    enabled: open && !!tenant && !!event?.project_id,
    queryFn: async () => {
      const res = await fetch(
        `/api/v1/calendar/forty-detail?tenant=${encodeURIComponent(tenant)}&project_id=${event!.project_id}`,
        { credentials: "include" },
      )
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
  })

  useEffect(() => {
    if (open) { setEdit(false); setErr(null) }
  }, [open, event?.project_id])
  useEffect(() => {
    if (detail.data) {
      setStreet(detail.data.street || "")
      setZip(detail.data.zip || "")
      setCity(detail.data.city || "")
    }
  }, [detail.data])

  const save = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/v1/calendar/forty-adresse`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ tenant, project_id: event!.project_id, street, zipcode: zip, city }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || json.error) throw new Error(json.error || `HTTP ${res.status}`)
      return json
    },
    onSuccess: () => {
      toast("✓ Adresse aktualisiert")
      qc.invalidateQueries({ queryKey: ["forty-detail", tenant, event?.project_id] })
      qc.invalidateQueries({ queryKey: ["calendar"] })
      setEdit(false)
    },
    onError: (e) => {
      const m = e instanceof Error ? e.message : String(e)
      setErr(m); toast("Speichern fehlgeschlagen: " + m, "error")
    },
  })

  if (!open || !event) return null
  const d = detail.data
  const istObjekt = !!d?.facility_id
  const zielLabel = istObjekt ? "Objekt-Adresse" : "Kunden-Adresse"

  return createPortal(
    <div
      className="pp-overlay oben"
      data-portal-modal
      style={{
        position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "24px 16px", overflowY: "auto", backdropFilter: "blur(4px)",
      }}
      onMouseDown={(ev) => { if (ev.target === ev.currentTarget) onClose() }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-card)", borderRadius: 12, padding: 24,
          maxWidth: 460, width: "100%", margin: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,.2), 0 0 0 1px rgba(0,0,0,.05)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Einsatz (fortytools)</h3>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 4 }}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: event.in_ours ? "#059669" : "var(--text-muted)" }}>
            <CheckCircle className="w-3.5 h-3.5" />
            {event.in_ours ? "In fortytools und unserem Kalender" : "Nur in fortytools"}
          </div>
          {d?.web_link && (
            <a href={d.web_link} target="_blank" rel="noopener noreferrer"
               style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--accent-color)", fontWeight: 600 }}>
              In fortytools öffnen <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>

        <Row icon={<Clock className="w-4 h-4" />} label="Zeit">
          {fmt(event.start)}{event.end ? ` – ${new Date(event.end).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}` : ""}
        </Row>
        <Row icon={<User className="w-4 h-4" />} label="Monteur">
          {event.monteur_name || <span style={{ color: "var(--text-muted)" }}>Ohne Zuordnung</span>}
        </Row>
        <Row icon={<User className="w-4 h-4" />} label="Kunde">
          {detail.isLoading ? "…" : d?.kunde_name
            ? <>{d.kunde_name}{d.kunde_number != null && <span style={{ color: "var(--text-muted)" }}> · #{d.kunde_number}</span>}</>
            : <span style={{ color: "var(--text-muted)" }}>—</span>}
        </Row>
        {d?.objekt_name && (
          <Row icon={<Building2 className="w-4 h-4" />} label="Objekt">{d.objekt_name}</Row>
        )}

        {/* Adresse — anzeigen oder bearbeiten */}
        <div style={{ marginTop: 4, marginBottom: 8, padding: 12, background: "var(--secondary-bg)", borderRadius: 8 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: edit ? 10 : 4 }}>
            <div style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.4, display: "flex", alignItems: "center", gap: 5 }}>
              <MapPin className="w-3.5 h-3.5" /> {zielLabel}
            </div>
            {!edit && d && (
              <button type="button" onClick={() => setEdit(true)} style={{ background: "none", border: "none", color: "var(--accent-color)", cursor: "pointer", fontSize: 12 }}>Bearbeiten</button>
            )}
          </div>
          {err && <div className="pp-alert pp-alert-err" style={{ marginBottom: 8 }}>{err}</div>}
          {edit ? (
            <>
              <input value={street} onChange={(e) => setStreet(e.target.value)} placeholder="Straße" style={{ width: "100%", marginBottom: 8 }} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 8, marginBottom: 10 }}>
                <input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="PLZ" />
                <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Stadt" />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button type="button" onClick={() => { setEdit(false); setErr(null) }} className="pp-btn pp-btn-sm">Abbrechen</button>
                <button type="button" onClick={() => save.mutate()} disabled={save.isPending} className="pp-btn pp-btn-primary pp-btn-sm">
                  {save.isPending ? "Speichert…" : "Speichern"}
                </button>
              </div>
            </>
          ) : (
            <div style={{ fontSize: 14 }}>
              {[street, [zip, city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || <span style={{ color: "var(--text-muted)" }}>{event.location || "—"}</span>}
            </div>
          )}
        </div>

        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 8 }}>
          Der Einsatz selbst wird in fortytools gepflegt und ist hier nicht verschiebbar.
        </div>
      </div>
    </div>,
    document.body,
  )
}

function Row({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12 }}>
      <span style={{ color: "#94a3b8", marginTop: 2, flexShrink: 0 }}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
        <div style={{ fontSize: 14 }}>{children}</div>
      </div>
    </div>
  )
}
